import { HttpClient, HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import * as ContentAction from '@store/actions/content.actions';
import * as DataAction from '@store/actions/data.actions';
import * as FeaturingActions from '@store/actions/featuring.actions';
import * as MediaGalleryAction from '@store/actions/media-gallery.actions';
import * as ProfileAction from '@store/actions/profile.actions';
import * as UserAction from '@store/actions/users.actions';
import jwtDecode from 'jwt-decode';
import { MetaData, NgEventBus } from 'ng-event-bus';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, catchError, concatMap, filter, Observable, of, switchMap, take, throwError } from 'rxjs';
import { EBEvt } from '../shared/eb-evt';

const COOKIE_EXP_DATE: Date = new Date(new Date().setDate(new Date().getDate() + 30));
const ACCESS_KEY: string = environment.access_token_key;
const REFRESH_KEY: string = environment.refresh_token_key;
const DEF_OPTS = {
  domain: `${environment.cookie_domain}`,
  path: '/',
  expires: COOKIE_EXP_DATE,
};
const DEF_HEADERS = { 'INTERCONN-KEY': environment.interconn_key };

export interface ITokens {
  access: string;
  refresh?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccessTokenService {
  private readonly evtbus = inject(NgEventBus);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly cookieService = inject(CookieService);
  private readonly store = inject(Store);

  private token_refreshed$ = new BehaviorSubject<boolean>(false);
  private refreshing = false;
  private skip_refresh = false;

  constructor() {
    this.init_eb_events();
  }

  private init_eb_events() {
    this.evtbus.on(EBEvt.G_AUTH_SIGNOUT).subscribe(() => this.force_logout());
  }

  skipRefresh = (v: boolean) => (this.skip_refresh = v);

  getAuthString = (): string => `Bearer ${this.getAccessToken()}`;
  getAuthHeader = (): { Authorization: string } => ({ Authorization: this.getAuthString() });

  getReqWithAuth = (req: HttpRequest<any>): HttpRequest<any> =>
    this.getAccessToken()?.length ? req.clone({ setHeaders: this.getAuthHeader() }) : req;

  decode = (v: string): any => jwtDecode(v);
  refreshable = (): boolean => !!this.getRefreshToken()?.length;
  getAccessToken = (): string => this.cookieService.get(ACCESS_KEY) || '';
  getRefreshToken = (): string => this.cookieService.get(REFRESH_KEY) || '';

  setAccessToken = (token: string | null, opts?: any): void => {
    if (token) {
      this.cookieService.put(ACCESS_KEY, token, opts ?? DEF_OPTS);
      this.evtbus.cast(EBEvt.G_AUTH_TOKEN_ACCESS, token);
    } else {
      this.cookieService.remove(ACCESS_KEY, opts ?? DEF_OPTS);
    }
  };

  setRefreshToken = (token: string | null, opts?: any): void => {
    if (token) {
      this.cookieService.put(REFRESH_KEY, token, opts ?? DEF_OPTS);
      this.evtbus.cast(EBEvt.G_AUTH_TOKEN_REFRESH, token);
    } else {
      this.cookieService.remove(REFRESH_KEY, opts ?? DEF_OPTS);
    }
  };

  public clearAuth = (): void => {
    this.setAccessToken(null);
    this.setRefreshToken(null);
  };

  public force_reload = (): void => {
    location.reload();
  };

  public force_logout = (): void => {
    this.store.dispatch(ProfileAction.userLogout());
    this.store.dispatch(DataAction.clearData());
    this.store.dispatch(ContentAction.clearContent());
    this.store.dispatch(FeaturingActions.clearFeaturing());
    this.store.dispatch(UserAction.clearUser());
    this.store.dispatch(MediaGalleryAction.clear());
    sessionStorage.clear();
    this.clearAuth();
    this.force_reload();
  };

  public getTokenUser(): any {
    try {
      const token = this.getAccessToken();
      return token ? jwtDecode<any>(token)?.subject : null;
    } catch (err) {
      console.error('AccessTokenService.getTokenUser(): Error:', err);
      return null;
    }
  }

  public refresh_token(): Observable<ITokens> {
    const refresh = this.getRefreshToken();
    if (!refresh) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.http.post<ITokens>(`${environment.token_url}/refresh`, { refresh }, { headers: DEF_HEADERS }).pipe(
      catchError((err) => {
        console.error('[AccessTokenService] Token refresh error:', err);
        return throwError(() => err);
      }),
    );
  }

  public process_request(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip refresh if not needed
    if (this.skip_refresh || !this.refreshable()) {
      return next.handle(this.getReqWithAuth(req));
    }

    // Skip if refreshing
    if (this.refreshing) {
      return this.token_refreshed$.pipe(
        filter((res) => res === true),
        take(1),
        switchMap(() => next.handle(this.getReqWithAuth(req))),
      );
    }

    this.refreshing = true;
    this.token_refreshed$.next(false);

    console.error('[AccessTokenService] Refreshing...');
    return this.refresh_token().pipe(
      switchMap((tokens: ITokens) => {
        if (!tokens?.access) {
          throw new Error('Invalid token format');
        }

        this.setAccessToken(tokens.access);
        if (tokens.refresh) {
          this.setRefreshToken(tokens.refresh);
        }

        this.token_refreshed$.next(true);
        this.refreshing = false;

        return next.handle(this.getReqWithAuth(req));
      }),
      catchError((err) => {
        this.refreshing = false;
        this.token_refreshed$.next(false);

        if (err.status === 401 || err.status === 410) {
          this.force_logout();
        }

        // Continue even with invalid token
        return next.handle(this.getReqWithAuth(req));
      }),
    );
  }
}
